import type { RunState } from 'shared';

const SAVE_KEY_PREFIX = 'player_progress_';

export interface SavedProgress {
  scenarioId: string;
  state: RunState;
  savedAt: number;
  scenarioTitle?: string;
  /**
   * P9-1: このセーブが属するプレイスルーの一意 id（図鑑記録の冪等キー）。
   * 「続きから」で同一プレイスルーを維持するために保存する。旧セーブには無い（optional・後方互換）。
   */
  playthroughId?: string;
}

/**
 * シナリオIDに基づいた保存キーを生成
 */
function getSaveKey(scenarioId: string): string {
  return `${SAVE_KEY_PREFIX}${scenarioId}`;
}

/**
 * P9-2 Part C 後方互換: 修正前の ticketless 経路は全シナリオが共通 id 'mock-scenario' で
 * 保存されていた（bootstrap.ts が `previewId ?? 'mock-scenario'` を返していたため、
 * 別シナリオを開くとセーブが上書きされる衝突バグ）。この旧共有キーに残っているセーブを、
 * 「このシナリオのものだと確認できた場合のみ」新キーへ一度だけ移行する。
 */
const LEGACY_SHARED_SCENARIO_ID = 'mock-scenario';

/** 移行判定に使う最小限のシナリオ形状（ScenarioContent のサブセット）。 */
export interface LegacyMigrationScenarioShape {
  meta?: { title?: string };
  nodes?: Array<{ id?: string }>;
}

/**
 * 旧共有キー（player_progress_mock-scenario）のセーブを新キー（player_progress_<scenarioId>）へ移行する。
 * bootstrap の ticketless 経路で、シナリオ解決直後に 1 回呼ぶ。
 *
 * 所有シナリオの確認（旧キーは全シナリオ共有だったため、無条件に引き取ると
 * 「別シナリオのセーブを復元して壊れた状態から再開」する事故になる）:
 *   (a) 保存時の scenarioTitle が現シナリオの meta.title と一致する、または
 *   (b) セーブの currentNodeId が現シナリオの node として実在する
 * のいずれかを満たすときだけ引き取る。(a)|(b) の OR にする理由: 過去の保存経路では
 * scenarioTitle がフォールバック文字列（'Demo Scenario' 等）だった時期があり得るため、
 * タイトル不一致でも node 実在で救済する（各シナリオの node id は命名規約が別で衝突しない）。
 * どちらも満たさない場合は旧キーに残す（本来の持ち主シナリオが開かれた時に引き取る）。
 */
export function migrateLegacyProgress(
  scenarioId: string,
  scenario: LegacyMigrationScenarioShape | null | undefined
): void {
  try {
    if (!scenarioId || scenarioId === LEGACY_SHARED_SCENARIO_ID) return;
    // 新キーに既にセーブがある場合は何もしない（新しい進行が正）。
    if (localStorage.getItem(getSaveKey(scenarioId)) !== null) return;
    const raw = localStorage.getItem(getSaveKey(LEGACY_SHARED_SCENARIO_ID));
    if (!raw) return;

    const data = JSON.parse(raw) as SavedProgress;
    if (!data?.state || typeof data.state !== 'object') return;

    const title = scenario?.meta?.title;
    const titleMatches =
      typeof title === 'string' && title.length > 0 && data.scenarioTitle === title;
    const nodeIds = new Set(
      (scenario?.nodes ?? [])
        .map((node) => node?.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    );
    const nodeMatches =
      typeof data.state.currentNodeId === 'string' && nodeIds.has(data.state.currentNodeId);

    if (!titleMatches && !nodeMatches) return;

    localStorage.setItem(getSaveKey(scenarioId), JSON.stringify({ ...data, scenarioId }));
    localStorage.removeItem(getSaveKey(LEGACY_SHARED_SCENARIO_ID));
  } catch (error) {
    console.warn('Failed to migrate legacy progress:', error);
  }
}

/**
 * 進行状況を保存
 */
export function saveProgress(
  scenarioId: string,
  state: RunState,
  scenarioTitle?: string,
  playthroughId?: string
): void {
  try {
    const data: SavedProgress = {
      scenarioId,
      state,
      savedAt: Date.now(),
      scenarioTitle,
      playthroughId
    };
    localStorage.setItem(getSaveKey(scenarioId), JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save progress:', error);
  }
}

/**
 * 保存された進行状況を読み込む
 */
export function loadProgress(scenarioId: string): SavedProgress | null {
  try {
    const raw = localStorage.getItem(getSaveKey(scenarioId));
    if (!raw) return null;

    const data = JSON.parse(raw) as SavedProgress;

    // 基本的なバリデーション
    if (!data.state || typeof data.state !== 'object') {
      return null;
    }

    return data;
  } catch (error) {
    console.warn('Failed to load progress:', error);
    return null;
  }
}

/**
 * 2026-07-20 傷⑤: 復元互換の判定に使う最小限のシナリオ形状（ScenarioContent のサブセット）。
 */
export interface ProgressCompatScenarioShape {
  nodes?: Array<{ id?: string; steps?: Array<unknown> }>;
}

/**
 * 2026-07-20 傷⑤: 保存済み進行が「現行シナリオ構造の上で再開できるか」を判定する。
 *
 * 背景: loadProgress() は型チェックのみで、保存済み currentNodeId / currentStepIndex が
 * 現行シナリオに実在するかを検証しない。シナリオ改稿で node が消えたり steps が短くなった
 * セーブを復元すると、StepEngine は currentStep=null（waiting=null・completed=false）で
 * 静止し、画面は「……準備中」固定＝復元詰みになる。
 *
 * 判定規約（StepEngine / normalizeRunState の実挙動に合わせる）:
 * - state が object でない → 非互換（壊れたセーブ）。
 * - currentNodeId が null/undefined → 互換。normalizeRunState が start node に解決するため
 *   「最初から」相当で安全に再開できる（完了状態のセーブ）。
 * - currentNodeId が string 以外 → 非互換。
 * - scenario 未指定（null/undefined）→ 互換扱い。検証材料がないため従来挙動に委ねる
 *   （restoreState 側は scenario 未ロード時に no-op する）。
 * - currentNodeId が scenario.nodes に実在しない → 非互換。
 * - 該当 node の steps が空 → 非互換（getCurrentStep が null になり「準備中」固定）。
 * - currentStepIndex: 非有限値は normalizeRunState と同じく 0 とみなした上で、
 *   整数かつ 0 <= index < steps.length を要求（小数は steps[1.5]=undefined で詰むため非互換）。
 */
export function isProgressCompatible(
  state: RunState | null | undefined,
  scenario: ProgressCompatScenarioShape | null | undefined
): boolean {
  if (!state || typeof state !== 'object') return false;

  const nodeId = (state as { currentNodeId?: unknown }).currentNodeId;
  if (nodeId === null || nodeId === undefined) return true; // 完了状態＝start node へ正規化される
  if (typeof nodeId !== 'string') return false;
  if (!scenario) return true; // 検証不能（シナリオ未ロード）は従来挙動に委ねる

  const node = (scenario.nodes ?? []).find((n) => n?.id === nodeId);
  if (!node) return false;

  const steps = Array.isArray(node.steps) ? node.steps : [];
  if (steps.length === 0) return false;

  const rawIndex = (state as { currentStepIndex?: unknown }).currentStepIndex;
  const index =
    typeof rawIndex === 'number' && Number.isFinite(rawIndex) ? rawIndex : 0;
  return Number.isInteger(index) && index >= 0 && index < steps.length;
}

/**
 * 保存された進行状況があるかチェック
 */
export function hasProgress(scenarioId: string): boolean {
  try {
    const key = getSaveKey(scenarioId);
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

/**
 * 保存された進行状況を削除
 */
export function clearProgress(scenarioId: string): void {
  try {
    localStorage.removeItem(getSaveKey(scenarioId));
  } catch (error) {
    console.warn('Failed to clear progress:', error);
  }
}

/**
 * 2026-08-07 副症状修正: `?fresh=1` は「初回起動時に一度だけ進行を消す」ワンショット指定。
 * URL に残したままだと、リロードやブラウザバック（同一エントリの再読込）のたびに
 * 進行が全消しされる（2026-08-07 商談デモで実発生）。適用後に search 文字列から除去するための
 * 純関数。fresh 以外のパラメータと順序は保持する。
 *
 * @returns fresh を除去した search 文字列（例 '?scenario=x&prod=1'、全消えなら ''）。
 *          fresh が存在しない場合は null（呼び出し側は replaceState 不要）。
 *          `?reset=1`（開発用）は対象外＝従来挙動のまま。
 */
export function stripFreshParamFromSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  if (!params.has('fresh')) return null;
  params.delete('fresh');
  const next = params.toString();
  return next ? `?${next}` : '';
}

/**
 * 保存データの概要を取得（表示用）
 */
export function getProgressSummary(scenarioId: string): { savedAt: Date; nodeId?: string } | null {
  const data = loadProgress(scenarioId);
  if (!data) return null;

  return {
    savedAt: new Date(data.savedAt),
    nodeId: data.state.currentNodeId ?? undefined
  };
}
